import React from 'react'
// hooks
import { useShowModal } from 'hooks'
// constants
import { Modals } from 'constants/modals'
// components
import Select from '../Select'
import RowCell from '../../RowCell'
import PopoverModal from 'components/UI/PopoverModal'
// icons
import MenuItemOrigin from '@mui/material/MenuItem'
// utils
import { get } from 'lodash'
// types
import { RowCellValueChangeFn } from '../../../index'
import { MatrixOption, TMatrix } from '../../../types'

const MODAL_PROPS = {
  anchorOrigin: {
    vertical: 'center' as const,
    horizontal: 'center' as const
  },
  transformOrigin: {
    vertical: 'top' as const,
    horizontal: 'left' as const
  }
}

interface Props {
  idx: number
  value: string | MatrixOption
  cellId: string
  cellIdx: number
  setData: React.Dispatch<React.SetStateAction<TMatrix>>
  options: MatrixOption[]
  disabled: boolean
  selected: string
  onCellValueChange: RowCellValueChangeFn
  onOptionsSelect: (id: string) => Promise<void>
}

const RightSectionCell: React.FC<Props> = ({
  idx,
  value,
  cellId,
  cellIdx,
  setData,
  options,
  disabled,
  selected,
  onOptionsSelect,
  onCellValueChange
}) => {
  const { showModal } = useShowModal({ modalType: Modals.RISK_MATRIX_OPTIONS_MODAL })

  const showEditMatrixOptionsModal = (isSelect: boolean) => {
    isSelect && showModal()
  }

  const onOptionClick = (id: string, idx: number, option: MatrixOption, closePopup: () => void) => () => {
    setData((prev) => ({
      ...prev,
      bRightSection: prev.bRightSection.map((row, cellIdx) => {
        if (cellIdx === idx) {
          return row.map((subRow) => {
            if (subRow.id === id) {
              return { ...subRow, value: option.id }
            }
            return subRow
          })
        }
        return row
      })
    }))

    closePopup()
  }

  const onSubOptionClick =
    (cellId: string) =>
    ({
      event,
      option,
      isSelect,
      closePopup
    }: {
      event: React.MouseEvent<HTMLElement>
      option: string
      isSelect: boolean
      closePopup: () => void
    }) => {
      event.stopPropagation()

      if (option === 'Edit Options') {
        showEditMatrixOptionsModal(isSelect)
        closePopup()
        return
      }

      onOptionsSelect(cellId)
      closePopup()
    }

  const isSelect = cellIdx !== 0

  if (!isSelect) {
    return (
      <RowCell
        value={value as string}
        cellId={cellId}
        disabled={disabled}
        isRowHeader={cellIdx == 0}
        maxLines={cellIdx == 0 ? 3 : 5}
        onCellValueChange={onCellValueChange(idx, cellId)}
      />
    )
  }

  const color: string = get(value, 'color') || options.find(({ id }) => id === value)?.color
  const rowValueLabel: string = get(value, 'label') || options.find(({ id }) => id === value)?.label

  return (
    <PopoverModal
      {...MODAL_PROPS}
      trigger={
        <div>
          <Select
            color={color}
            isSelect={isSelect}
            showOptions={!disabled}
            rowValueLabel={rowValueLabel}
            isSelected={selected === cellId}
            onSubOptionClick={onSubOptionClick(cellId)}
          />
        </div>
      }>
      {({ closePopup, open }) => {
        if (!disabled && open && options.length === 0) {
          showModal()
          closePopup()
        }

        if (disabled) {
          if (open) {
            if (rowValueLabel) {
              onOptionsSelect(cellId)
            }
            closePopup()
          }
          return null
        }

        return options.map((option) => (
          <MenuItemOrigin key={option.value} onClick={onOptionClick(cellId, idx, option, closePopup)}>
            <span>{option.label}</span>
          </MenuItemOrigin>
        ))
      }}
    </PopoverModal>
  )
}

export default RightSectionCell
